"use client";
/**
 * One exercise as its own object, in whichever of the three compositions is
 * selected.
 *
 * A Record rather than a switch: adding a variant is one member and one entry,
 * and the compiler refuses a member without a component.
 *
 * Both selections come from the shared variant registry, so the tweak panel
 * drives every family through one provider instead of one context each. An
 * explicit prop still wins, because an embedder that pins a composition — a
 * deliverable, a test, a screenshot — is not asking the panel's opinion.
 */
import type { ComponentType, ReactElement } from "react";
import { useCresultcardVariant } from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_CARD_VARIANTS,
  C_WORKOUT_CARD_VARIANT_GROUP,
  C_WORKOUT_MOVE_READINGS,
  C_WORKOUT_MOVE_READING_GROUP,
} from "./cworkoutcard.constants";
import { CworkoutcardCoachingCard } from "./CworkoutcardCoachingCard";
import { CworkoutcardDemoFrame } from "./CworkoutcardDemoFrame";
import { CworkoutcardLoadBoard } from "./CworkoutcardLoadBoard";
import {
  CworkoutcardVariant,
  type CworkoutcardProps,
  type CworkoutcardVariantProps,
} from "./cworkoutcard.types";

const VARIANT_COMPONENT: Record<
  CworkoutcardVariant,
  ComponentType<CworkoutcardVariantProps>
> = {
  [CworkoutcardVariant.DemoFrame]: CworkoutcardDemoFrame,
  [CworkoutcardVariant.CoachingCard]: CworkoutcardCoachingCard,
  [CworkoutcardVariant.LoadBoard]: CworkoutcardLoadBoard,
};

export function Cworkoutcard({
  data,
  variant,
  moveReading,
  openControl,
}: CworkoutcardProps): ReactElement {
  const { value: registryVariant } = useCresultcardVariant({
    group: C_WORKOUT_CARD_VARIANT_GROUP,
    options: C_WORKOUT_CARD_VARIANTS,
  });
  const { value: registryReading } = useCresultcardVariant({
    group: C_WORKOUT_MOVE_READING_GROUP,
    options: C_WORKOUT_MOVE_READINGS,
  });
  const VariantComponent = VARIANT_COMPONENT[variant ?? registryVariant];

  return (
    <VariantComponent
      data={data}
      moveReading={moveReading ?? registryReading}
      openControl={openControl}
    />
  );
}
